"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type NavItem = { href: string; label: string };

export function MobileNav({ items }: { items: ReadonlyArray<NavItem> }) {
  const [open, setOpen] = useState(false);

  // Close the sheet on Escape and lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        aria-controls="mobile-nav-sheet"
        onClick={() => setOpen((v) => !v)}
        className="lg:hidden inline-flex size-11 items-center justify-center rounded-md text-[var(--fg)] hover:bg-white/[0.04] transition-colors"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          {open ? (
            <>
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </>
          ) : (
            <>
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </>
          )}
        </svg>
      </button>

      <div
        id="mobile-nav-sheet"
        className={cn(
          "lg:hidden fixed inset-x-0 top-[68px] z-30 border-b border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur-xl",
          "transition-[opacity,transform] duration-200 origin-top",
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none",
        )}
      >
        <nav className="flex flex-col p-4 gap-1">
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-md text-[var(--fg)] hover:bg-white/[0.04] transition-colors text-base"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Backdrop catches outside clicks. Sits below the sheet but above the page. */}
      <div
        aria-hidden="true"
        onClick={() => setOpen(false)}
        className={cn(
          "lg:hidden fixed inset-0 top-[68px] z-20 bg-black/40 transition-opacity duration-200",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        )}
      />
    </>
  );
}
